import { CollapsibleEvent } from "../events/CollapsibleEvent.js";

export class Collapsible {
    public constructor(
        private container: HTMLElement,
        private toggler: HTMLElement,
        private panel: HTMLElement,
    ) {
        this.toggler.addEventListener("click", () => this.toggle());
        this.panel.addEventListener("keydown", ({ key }) => {
            if (this.active && key === "Escape") this.close();
        });
        if (this.dependent)
            document.addEventListener(CollapsibleEvent.KEY, (event) => {
                if (
                    event.detail.action === "open" &&
                    event.detail.context === this.context &&
                    event.detail.id !== this.id
                ) {
                    this.close();
                }
            });
    }

    public static create(
        id: string,
        container: HTMLElement = document.body,
    ): Collapsible {
        const toggler = container.querySelector<HTMLElement>(
            `[aria-controls="${id}"]`,
        );
        const panel = container.querySelector<HTMLElement>(`#${id}`);
        if (!toggler || !panel)
            throw new Error(
                `Elements for collapsible not found using ID ${id}`,
            );
        return new Collapsible(container, toggler, panel);
    }

    public open(): void {
        this.setExpanded(true);
        if (this.dependent)
            document.dispatchEvent(
                new CollapsibleEvent({
                    detail: {
                        action: "open",
                        context: this.context,
                        id: this.id,
                    },
                }),
            );
    }

    public close(): void {
        this.setExpanded(false);
    }

    public setExpandedMode(expanded: boolean): void {
        this.container.toggleAttribute("data-collapsible-expanded", expanded);
        this.toggler.toggleAttribute("disabled", expanded);
        if (expanded) {
            this.container.removeAttribute("role");
            this.toggler.removeAttribute("role");
            this.panel.removeAttribute("role");
            this.panel.removeAttribute("aria-labelledby");
        } else {
            this.container.setAttribute("role", "tablist");
            this.toggler.setAttribute("role", "tab");
            this.panel.setAttribute("role", "tabpanel");
            this.panel.setAttribute("aria-labelledby", this.toggler.id);
        }
        this.setExpanded(expanded);
    }

    public toggle(): void {
        this.active ? this.close() : this.open();
    }

    private get id(): string {
        return this.panel.id;
    }
    private get context(): string {
        return (
            this.container.getAttribute("context") ??
            this.container.getAttribute("collapsible") ??
            ""
        );
    }
    private get dependent(): boolean {
        return this.container.hasAttribute("context");
    }
    private get active(): boolean {
        return (
            !this.panel.hidden &&
            this.toggler.getAttribute("aria-expanded") === "true"
        );
    }

    private setExpanded(expanded: boolean): void {
        this.toggler.setAttribute("aria-expanded", expanded ? "true" : "false");
        this.panel.hidden = !expanded;
    }
}
